import { useState } from "react";
import axios from "axios";
import {
  Send, RefreshCw, CheckCircle2, AlertCircle, Smartphone, Apple, Wallet,
} from "lucide-react";
import { API } from "../../config";

const PlatformBadge = ({ platform }) => {
  if (platform === "apple") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-gray-300">
        <Apple className="w-3 h-3" /> Apple
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-blue-300">
      <Wallet className="w-3 h-3" /> Google
    </span>
  );
};

const ResultRow = ({ platform, result }) => {
  if (!result.attempted) {
    return (
      <div className="flex items-center gap-2 py-1.5 text-[11px] text-gray-500">
        <PlatformBadge platform={platform} />
        <span>Skipped</span>
      </div>
    );
  }
  const Icon = result.sent ? CheckCircle2 : AlertCircle;
  const color = result.sent ? "text-emerald-300" : "text-amber-300";
  return (
    <div
      className="flex items-start gap-2 py-1.5"
      data-testid={`tp-result-${platform}`}
    >
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color}`} />
      <div className="flex-1 min-w-0">
        <PlatformBadge platform={platform} />
        <p className="text-gray-300 text-[11px] leading-relaxed mt-0.5">
          {result.detail}
        </p>
      </div>
    </div>
  );
};

export const WalletTestPushWidget = () => {
  const [customerId, setCustomerId] = useState("");
  const [platform, setPlatform] = useState("both");
  const [customMessage, setCustomMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!customerId.trim()) {
      setError("Inserisci un Customer ID valido.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const body = { platform };
      if (customMessage.trim()) body.custom_message = customMessage.trim();
      const r = await axios.post(
        `${API}/admin/wallet/test-push/${encodeURIComponent(customerId.trim())}`,
        body,
      );
      setResult(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(
        typeof d === "string" ? d :
          (d?.msg || e?.message || "Errore inviando il test push")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6"
      data-testid="wallet-test-push-widget"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
          <Send className="w-5 h-5 text-cyan-300" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-base">
            Test Push · 1 Customer
          </h3>
          <p className="text-gray-500 text-xs">
            Manda una notifica push test ad un SOLO customer senza inquinare la fleet — utile per debug e validazione post-deploy
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="md:col-span-2">
          <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
            Customer ID
          </label>
          <input
            type="text"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            placeholder="es. abc12345-1234-..."
            className="w-full bg-[#141414] border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 font-mono placeholder-gray-600 focus:outline-none focus:border-cyan-500/50"
            data-testid="tp-customer-id"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
            Piattaforma
          </label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="w-full bg-[#141414] border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-500/50"
            data-testid="tp-platform"
          >
            <option value="both">Apple + Google</option>
            <option value="google">Solo Google</option>
            <option value="apple">Solo Apple</option>
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
          Messaggio custom (opzionale, max 200 char · solo Google)
        </label>
        <input
          type="text"
          value={customMessage}
          onChange={(e) => setCustomMessage(e.target.value.slice(0, 200))}
          placeholder="Lascia vuoto per usare il default i18n"
          maxLength={200}
          className="w-full bg-[#141414] border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/50"
          data-testid="tp-custom-message"
        />
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={loading || !customerId.trim()}
        className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold px-4 py-2 rounded-lg text-sm"
        data-testid="tp-submit"
      >
        {loading ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" /> Invio in corso…
          </>
        ) : (
          <>
            <Send className="w-4 h-4" /> Manda Test Push
          </>
        )}
      </button>

      {error && (
        <div className="mt-4 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-300 text-xs" data-testid="tp-error">{error}</p>
        </div>
      )}

      {result && (
        <div
          className="mt-4 bg-[#0F0F0F] border border-gray-800 rounded-xl p-4"
          data-testid="tp-result"
        >
          <div className="flex items-center gap-2 mb-3 text-xs">
            <Smartphone className="w-4 h-4 text-cyan-300" />
            <span className="text-gray-200 font-semibold">
              {result.customer_name}
            </span>
            <span className="text-gray-500 font-mono text-[10px]">
              {result.customer_id} · {result.customer_country || "—"}
            </span>
          </div>
          <div className="divide-y divide-gray-800/60">
            <ResultRow platform="google" result={result.google} />
            <ResultRow platform="apple" result={result.apple} />
          </div>
        </div>
      )}
    </div>
  );
};
